import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import '../styles/views/home.css';
import { formatEmployeeExperience } from '../utils/employee';
import { DEFAULT_HOMEPAGE_STUDIO } from '../utils/homepage';

export default function Home({ onNavigate, initialSector = 'all' }) {
  const { data: siteSettings } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const res = await fetch('/api/settings');
      if (!res.ok) throw new Error('Não foi possível carregar as configurações do site.');
      return res.json();
    },
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects', 'published'],
    queryFn: async () => {
      const res = await fetch('/api/projects');
      return res.json();
    }
  });

  const { data: teamData } = useQuery({
    queryKey: ['employees'],
    queryFn: async () => {
      const res = await fetch('/api/employees');
      return res.json();
    }
  });

  const [activeSector, setActiveSector] = useState(initialSector);
  useEffect(() => setActiveSector(initialSector), [initialSector]);
  const projects = projectsData?.projects || [];
  const employees = teamData?.employees || [];
  const studio = { ...DEFAULT_HOMEPAGE_STUDIO, ...siteSettings?.homepage_studio };
  const founder = employees.find(e => e.id === 'maria-gouvea') || employees[0];
  const featuredProject = projects.find((project) => Boolean(Number(project.featured)));
  const projectFilters = [
    { id: 'all', label: 'Todos' },
    { id: 'residential', label: 'Residencial' },
    { id: 'commercial', label: 'Comercial' },
    { id: 'expography', label: 'Expografia' }
  ];
  const filteredProjects = projects.filter((project) => activeSector === 'all'
    || project.sector === activeSector
    || (activeSector === 'expography' && project.sector === 'museum'));
  const projectYear = (project) => project.year || (project.created_at ? new Date(project.created_at).getFullYear() : '');
  const sectorLabel = (sector) => sector === 'museum' || sector === 'expography'
    ? 'Expografia'
    : sector === 'residential' ? 'Residencial' : 'Comercial';

  return (
    <div>
      {/* Hero Section */}
      <section className="hero">
        <img
          className="hero__img"
          src={siteSettings?.homepage_hero_image || 'https://images.unsplash.com/photo-1577495508048-b635879837f1?auto=format&fit=crop&w=1600&q=80'}
          alt="Memorial Ferroviário de Caruaru"
        />
        <div className="hero__card">
          <p className="eyebrow">Arquitetura &amp; Expografia</p>
          <h1 className="font-display hero__title">
            Arquitetura e expografia, do acervo à casa
          </h1>
          <p className="hero__lede">
            Projetos museológicos, expográficos, residenciais e comerciais em Pernambuco e na Paraíba, do conceito ao acompanhamento da obra.
          </p>
          <div className="hero__actions">
            <a
              href="/#projetos"
              onClick={(event) => { event.preventDefault(); onNavigate('/#projetos'); }}
              className="btn btn--solid"
            >
              Ver projetos <ArrowRight size={16} />
            </a>
            <a
              href="/#contato"
              onClick={(event) => { event.preventDefault(); onNavigate('/#contato'); }}
              className="btn btn--outline"
            >
              Fale com o estúdio
            </a>
          </div>
        </div>
      </section>

      {/* Studio Intro */}
      <section className="section section--sm" id="estudio">
        <div className="studio__grid">
          <div className="studio__copy">
            <p className="eyebrow">{studio.eyebrow}</p>
            <h2 className="font-display section-title">
              {studio.title}
            </h2>
            {studio.paragraph_one && <p className="studio__text">{studio.paragraph_one}</p>}
            {studio.paragraph_two && <p className="studio__text">{studio.paragraph_two}</p>}
          </div>
          {studio.image_url && (
            <figure className="studio__media">
              <img className="studio__img" src={studio.image_url} alt={studio.image_alt || ''} />
              {(studio.image_caption || studio.image_location) && (
                <figcaption className="font-label studio__caption">
                  {studio.image_caption}
                  {studio.image_caption && studio.image_location ? ' ' : ''}
                  {studio.image_location && <span className="studio__caption-location">{studio.image_location}</span>}
                </figcaption>
              )}
            </figure>
          )}
        </div>
      </section>

      {/* Horizontal Gallery Strip */}
      <section className="gallery-strip">
        <div className="gallery-strip__track">
          {projects.map((proj) => (
            <figure
              key={proj.id}
              onClick={() => onNavigate(`/projects/${proj.slug}`)}
              className="gallery-strip__item"
            >
              <img className="gallery-strip__img" src={proj.cover_image} alt={proj.title} />
              <figcaption className="font-label gallery-strip__caption">
                {proj.title} — <span className="gallery-strip__caption-sector">{proj.sector.toUpperCase()}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Services Section with Accordions */}
      <section className="services" id="servicos">
        <div className="container services__grid">
          <div className="services__intro">
            <p className="eyebrow">Serviços</p>
            <h2 className="font-display section-title">Do acervo ao espaço construído</h2>
          </div>
          <div className="services__list">
            {[
              { title: 'Projeto museológico e museográfico', desc: 'Organização do discurso expositivo e do acervo em parceria com a museologia, com definição do circuito, dos núcleos temáticos e da forma de apresentar cada peça.' },
              { title: 'Projeto expográfico', desc: 'Desenho do percurso, do mobiliário expositivo, da iluminação e da comunicação visual, do conceito ao projeto executivo.' },
              { title: 'Direção de arte', desc: 'Linguagem visual da exposição, da paleta de cores aos painéis e módulos interativos.' },
              { title: 'Arquitetura residencial e comercial', desc: 'Projetos de residências, lojas, cafés e escritórios, com atenção ao detalhamento e à execução.' },
              { title: 'Montagem e acompanhamento', desc: 'Acompanhamento da produção e da montagem, com coordenação dos fornecedores até a entrega.' }
            ].map((s, idx) => (
              <details key={idx} className="service" open={idx === 0}>
                <summary className="font-display service__summary">
                  <span className="service__summary-label">
                    {s.title}
                  </span>
                </summary>
                <p className="service__body">
                  {s.desc}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Featured project, managed from the admin panel */}
      {featuredProject && (
        <section className="feature" id="destaque">
          <div className="container feature__inner">
            <div className="feature__text">
              <p className="eyebrow">Projeto em destaque</p>
              <h2 className="font-display section-title">{featuredProject.title}</h2>
              {featuredProject.summary && <p>{featuredProject.summary}</p>}
              <dl className="feature__facts">
                {featuredProject.city && <><dt>Local</dt><dd>{featuredProject.city}</dd></>}
                <dt>Setor</dt><dd>{sectorLabel(featuredProject.sector)}</dd>
                {projectYear(featuredProject) && <><dt>Ano</dt><dd>{projectYear(featuredProject)}</dd></>}
              </dl>
              <button className="btn btn--outline feature__action" onClick={() => onNavigate(`/projects/${featuredProject.slug}`)}>
                Ver projeto <ArrowRight size={16} />
              </button>
            </div>
            <img className="feature__img" src={featuredProject.cover_image} alt={featuredProject.title} />
          </div>
        </section>
      )}

      {/* Projects Grid Section */}
      <section className="section section--lg" id="projetos">
        <div className="projects-preview">
          <div className="projects-preview__header">
            <div>
              <p className="eyebrow">Projetos</p>
              <h2 className="font-display section-title">Residencial, comercial e expografia</h2>
            </div>
          </div>

          <div className="projects-preview__filters" role="group" aria-label="Filtrar projetos por tipo">
            {projectFilters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className="project-filter"
                aria-pressed={activeSector === filter.id}
                onClick={() => setActiveSector(filter.id)}
              >
                {filter.label} <span>{filter.id === 'all' ? projects.length : projects.filter((project) => project.sector === filter.id || (filter.id === 'expography' && project.sector === 'museum')).length}</span>
              </button>
            ))}
          </div>

          <div className="projects-preview__grid">
            {filteredProjects.map((proj) => (
              <div key={proj.id} onClick={() => onNavigate(`/projects/${proj.slug}`)} className="project-card">
                <div className="project-card__media">
                  <img className="project-card__img" src={proj.cover_image} alt={proj.title} />
                </div>
                <span className="font-label project-card__type">
                  {sectorLabel(proj.sector)}
                </span>
                <h3 className="font-display project-card__title">
                  {proj.title}
                </h3>
                <span className="font-label project-card__meta">
                  {[proj.city, projectYear(proj)].filter(Boolean).join(' · ')}
                </span>
              </div>
            ))}
            {filteredProjects.length === 0 && <p className="projects-preview__empty">Ainda não há projetos publicados neste setor.</p>}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process" id="processo">
        <div className="container process__inner">
          <div className="process__intro">
            <p className="eyebrow">Processo</p>
            <h2 className="font-display section-title">Como trabalhamos</h2>
          </div>
          <ol className="process__list">
            {[
              { n: '01', title: 'Escuta e pesquisa', desc: 'Entendimento do acervo, do espaço e de quem vai usar, em diálogo com o cliente e, nos museus, com a museologia.' },
              { n: '02', title: 'Conceito e projeto', desc: 'Definição do percurso, da paleta e do mobiliário a partir das características do espaço.' },
              { n: '03', title: 'Projeto executivo', desc: 'Detalhamento do mobiliário, da iluminação e da comunicação visual para a execução.' },
              { n: '04', title: 'Acompanhamento', desc: 'Acompanhamento da produção, da obra e da montagem até a entrega.' }
            ].map((st, i) => (
              <li key={i} className="process__step">
                <span className="process__num">{st.n}</span>
                <h3 className="font-display process__title">{st.title}</h3>
                <p className="process__text">{st.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Clients Section */}
      <section className="section">
        <div className="clients__inner">
          <p className="eyebrow">Clientes e parceiros</p>
          <ul className="clients__list">
            {['Caixa Cultural', 'Prefeitura de Caruaru', 'Prefeitura de Triunfo', 'Prefeitura de Guarabira', 'Projeto Coral Vivo', 'Petrobras', 'Jorge Passos Arquitetura & Restauro', 'Fundação Joaquim Nabuco'].map((client, idx) => (
              <li key={idx} className="font-label clients__item">
                {client}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Founder Section */}
      {founder && (
        <section className="section">
          <div className="founder__grid">
            <figure className="founder__media">
              <img className="team-portrait founder__img" src={founder.photo_url} alt={founder.name} />
            </figure>
            <div className="founder__body">
              <p className="eyebrow">Quem faz</p>
              <h2 className="font-display section-title">{founder.name}</h2>
              <p className="font-label founder__role">{founder.role}</p>
              {formatEmployeeExperience(founder) && <p className="founder__experience">{formatEmployeeExperience(founder)}</p>}
              <p className="founder__bio">{founder.bio}</p>
            </div>
          </div>
        </section>
      )}

      {/* Team Roster Section */}
      <section className="section" id="equipe">
        <div className="team-roster">
          <div className="team-roster__header">
            <h2 className="font-display team-roster__title">Equipe</h2>
            <p className="team-roster__intro">A equipe dá suporte na elaboração de projetos executivos, no detalhamento e no acompanhamento da execução.</p>
          </div>
          <div className="team-grid team-grid--compact">
            {employees.map((emp) => (
              <div key={emp.id} className="team-card team-card--compact">
                <div className="team-card__media">
                  <img src={emp.photo_url || 'https://via.placeholder.com/400'} alt={emp.name} className="team-portrait team-card__img" />
                </div>
                <h3 className="font-display team-card__name">{emp.name}</h3>
                <p className="font-label team-card__role">{emp.role}</p>
                {formatEmployeeExperience(emp) && <p className="team-card__experience">{formatEmployeeExperience(emp)}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact" id="contato">
        <div className="container contact__grid">
          <div className="contact__intro">
            <p className="eyebrow">Contato</p>
            <h2 className="font-display section-title">Fale com o estúdio</h2>
            <p className="contact__lede">Conte um pouco sobre o acervo, o espaço ou a obra que você tem em mente.</p>
            <div className="contact__links">
              <a href="mailto:maria@auroraprojetos.com" className="contact__link">maria@auroraprojetos.com</a>
              <a href="tel:+5581994213135" className="contact__link">+55 81 99421-3135</a>
              <a href="https://www.instagram.com/auroraprojetos/" rel="noopener" className="contact__link">@auroraprojetos</a>
            </div>
            <p className="font-label contact__address">
              Av. Gov. Agamenon Magalhães, 2936, sala 1505<br />Espinheiro, Recife, PE, CEP 52020-000
            </p>
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); alert('Mensagem enviada com sucesso!'); }}
            className="contact__form"
          >
            <div className="form-field">
              <label className="font-label form-label">Nome</label>
              <input type="text" required className="form-control form-control--underline" />
            </div>
            <div className="contact__row">
              <div className="form-field contact__field">
                <label className="font-label form-label">E-mail</label>
                <input type="email" required className="form-control form-control--underline" />
              </div>
              <div className="form-field contact__field">
                <label className="font-label form-label">Telefone</label>
                <input type="tel" className="form-control form-control--underline" />
              </div>
            </div>
            <div className="form-field">
              <label className="font-label form-label">Sobre o projeto</label>
              <textarea rows={4} required className="form-control form-control--underline"></textarea>
            </div>
            <button type="submit" className="btn btn--solid contact__submit">
              Enviar mensagem
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
